import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import ProfileStyle from "../../assets/stylesheets/dashboard/profile.module.css";
import useAuthStore from "../../store/authStore";
import { ReactComponent as User} from '../../assets/icons/User.svg';
import { ReactComponent as Bookmark} from '../../assets/icons/Bookmark.svg';
import { ReactComponent as Favorite} from '../../assets/icons/Favorite.svg';
import { ReactComponent as Dashboard} from '../../assets/icons/Dashboard.svg';
import { ReactComponent as Notification} from '../../assets/icons/Notification.svg';
import { ReactComponent as Setting} from '../../assets/icons/Setting.svg';
import { ReactComponent as Lock} from '../../assets/icons/Lock.svg';
import { ReactComponent as myFarm} from '../../assets/icons/myFarm.svg';
import { ReactComponent as Search} from '../../assets/icons/search.svg';

export default function SideBar({ userName = "Farmciti", userSubtitle }) {
  const location = useLocation();
  const { role } = useAuthStore();

  const common = {
    profile: {
      to: "/dashboard/common/profile",
      label: "Personal Information",
      Icon: User,
      isActive: (pathname) => pathname === "/dashboard/common/profile" || pathname === "/profile",
    },
    password: {
      to: "/dashboard/common/password-security",
      label: "Password & Security",
      Icon: Lock,
      isActive: (pathname) => pathname === "/dashboard/common/password-security",
    },
    notification: {
      to: "/dashboard/common/notifications",
      label: "Notification",
      Icon: Notification,
      isActive: (pathname) => pathname === "/dashboard/common/notifications",
    },
    settings: {
      to: "/dashboard/common/settings",
      label: "Setting",
      Icon: Setting,
      isActive: (pathname) => pathname === "/dashboard/common/settings",
    },
      bookmarks: {
        to: "/dashboard/common/bookmarks",
        label: "My Bookmark",
        Icon: Bookmark,
        isActive: (pathname) => pathname === "/dashboard/common/bookmarks",
      },
      favorites: {
        to: "/dashboard/common/favorites",
        label: "My Favorites",
        Icon: Favorite,
        isActive: (pathname) => pathname === "/dashboard/common/favorites",
      },
  };

  const navByRole = {
    USER: [
      common.profile,
      common.bookmarks,
      common.favorites,
      common.password,
      common.notification,
      common.settings,
      
    ],
    FARMER: [
      common.profile,
      common.bookmarks,
      common.favorites,
      {
        to: "/dashboard/farmer/dashboard",
        label: "Farm Dashboard",
        Icon: myFarm,
        isActive: (pathname) => pathname === "/dashboard/farmer/dashboard",
      },
      common.password,
      common.notification,
      common.settings,
    ],
    ADMIN: [
      common.profile,
      common.bookmarks,
      common.favorites,
      {
        to: "/dashboard/admin/dashboard",
        label: "Dashboard",
        Icon: Dashboard,
        isActive: (pathname) => pathname === "/dashboard/admin/dashboard",
      },
      common.password,
      {
        to: "/dashboard/admin/blog",
        label: "Blog",
        Icon: Notification,
        isActive: (pathname) => pathname === "/dashboard/admin/blog",
      },
      {
        to: "/dashboard/admin/seo/farms",
        label: "Farm SEO",
        Icon: Search,
        isActive: (pathname) => pathname.startsWith("/dashboard/admin/seo"),
      },
      {
        label: "Listing",
        Icon: myFarm,
        isGroup: true,
        children: [
          {
            to: "/dashboard/admin/farm/listing",
            label: "Farm Listing",
            isActive: (pathname) => pathname.startsWith("/dashboard/admin/farm/listing"),
          },
          {
            to: "/dashboard/admin/event/listing",
            label: "Event Listing",
            isActive: (pathname) => pathname.startsWith("/dashboard/admin/event/listing"),
          },
        ],
      },
      common.notification,
      common.settings,
    ],
  };

  const navItems = navByRole[role] ?? navByRole.USER;
  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (label) => {
    setOpenGroups((prev) => {
      const isChildActive = navItems
        .find((item) => item.isGroup && item.label === label)
        ?.children?.some(({ isActive }) => isActive(location.pathname)) ?? false;
      const currentlyOpen = prev[label] === false ? false : (isChildActive || !!prev[label]);
      return { ...prev, [label]: !currentlyOpen };
    });
  };

  return (
    <div className={ProfileStyle["sidebar"]}>
      <div className={ProfileStyle["user-info"]}>
        <div className={ProfileStyle["avatar-wrapper"]} />
        <div className={ProfileStyle["user-details"]}>
          <h3 className={ProfileStyle["user-name"]}>{userName}</h3>
          {userSubtitle ? (
            <p className={ProfileStyle["user-email"]}>{userSubtitle}</p>
          ) : null}
        </div>
      </div>

      <div className={ProfileStyle["menu-list"]}>
        <h4 className={ProfileStyle["menu-title"]}>Menu</h4>

        {navItems.map((item) => {
          if (item.isGroup) {
            const isChildActive = item.children.some(({ isActive }) => isActive(location.pathname));
            // If user explicitly closed (false), respect that. Otherwise open when child is active.
            const manuallyClosed = openGroups[item.label] === false;
            const isOpen = !manuallyClosed && (isChildActive || !!openGroups[item.label]);
            const { Icon } = item;
            return (
              <div key={item.label}>
                <button
                  onClick={() => toggleGroup(item.label)}
                  className={`${ProfileStyle["menu-button"]} ${ProfileStyle["menu-button-inactive"]}`}
                  style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className={`${ProfileStyle["menu-icon"]} ${ProfileStyle["menu-icon-inactive"]}`} aria-hidden="true">
                      <Icon width={20} height={20} />
                    </span>
                    {item.label}
                  </span>
                  <span style={{ fontSize: 12 }}>{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && (
                  <div style={{ paddingLeft: 16 }}>
                    {item.children.map(({ to, label, isActive }) => {
                      const active = isActive(location.pathname);
                      return (
                        <Link
                          key={to}
                          to={to}
                          className={`${ProfileStyle["menu-button"]} ${active ? ProfileStyle["menu-button-active"] : ProfileStyle["menu-button-inactive"]}`}
                        >
                          {label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const { to, label, Icon, isActive } = item;
          const active = isActive(location.pathname);
          return (
            <Link
              key={to}
              to={to}
              className={`${ProfileStyle["menu-button"]} ${active ? ProfileStyle["menu-button-active"] : ProfileStyle["menu-button-inactive"]}`}
            >
              <span className={`${ProfileStyle["menu-icon"]} ${active ? ProfileStyle["menu-icon-active"] : ProfileStyle["menu-icon-inactive"]}`}
                aria-hidden="true"
              >
                <Icon width={20} height={20} />
              </span>
              {label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
